import React from "react";
import { AccountIdentity } from "@common/helpers/rules";
import { Button } from "@design-system/Button/Button";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { pluralizeWithCount } from "@common/helpers/string";
import { MultiEntityAccountCombobox } from "@components/accounts/MultiEntityAccountCombobox";
import { CloseIcon } from "@icons";
import { CategoryGlMappingOverrideRow } from "./useCategoryGlMappingGrid";
import {
    CategoryGlMappingDirection,
    WorkspaceAccount,
    accountToIdentity,
} from "./categoryGlMappingGrid.utils";
import styles from "./CategoryGlMappingGrid.module.scss";

interface Props {
    row: CategoryGlMappingOverrideRow;
    accounts: WorkspaceAccount[];
    accountsLoading: boolean;
    isDivergentDefault: boolean;
    onEntitiesChange: (entityIds: number[]) => void;
    onAccountChange: (
        direction: CategoryGlMappingDirection,
        identity: AccountIdentity | null,
    ) => void;
    onRemove: () => void;
}

export const CategoryGlMappingOverrideRowView: React.FC<Props> = ({
    row,
    accounts,
    accountsLoading,
    isDivergentDefault,
    onEntitiesChange,
    onAccountChange,
    onRemove,
}) => {
    const hasSelectedEntities = row.entityIds.length > 0;

    return (
        <div className={styles.row}>
            <div className={styles.entitiesCell}>
                <EntityFilterCombobox
                    filters={{ entityIds: row.entityIds }}
                    onChange={(filters) =>
                        onEntitiesChange(filters.entityIds ?? [])
                    }
                    onlyBusiness
                    mode="selection"
                    renderTrigger={({ triggerProps, selectedEntities }) => (
                        <Button
                            variant="primary"
                            size="md"
                            {...triggerProps}
                            type="button"
                        >
                            {selectedEntities.length ? (
                                <EntityAvatarGroup
                                    entities={selectedEntities}
                                    max={3}
                                    size="xs"
                                    label={pluralizeWithCount(
                                        "Entity",
                                        selectedEntities.length,
                                    )}
                                />
                            ) : (
                                "Select entities"
                            )}
                        </Button>
                    )}
                />
            </div>
            <div className={styles.accountCell}>
                {hasSelectedEntities ? (
                    <MultiEntityAccountCombobox
                        selectedAccountName={row.in?.name}
                        onChange={(account) =>
                            onAccountChange(
                                "in",
                                account ? accountToIdentity(account) : null,
                            )
                        }
                        selectedEntityIds={row.entityIds}
                        coverage="all"
                        accounts={accounts}
                        accountsLoading={accountsLoading}
                        size="md"
                    />
                ) : null}
            </div>
            <div className={styles.accountCell}>
                {hasSelectedEntities ? (
                    <MultiEntityAccountCombobox
                        selectedAccountName={row.out?.name}
                        onChange={(account) =>
                            onAccountChange(
                                "out",
                                account ? accountToIdentity(account) : null,
                            )
                        }
                        selectedEntityIds={row.entityIds}
                        coverage="all"
                        accounts={accounts}
                        accountsLoading={accountsLoading}
                        size="md"
                    />
                ) : null}
            </div>
            <div className={styles.removeCell}>
                <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove row"
                >
                    <CloseIcon />
                </Button>
            </div>
            {isDivergentDefault ? (
                <p className={styles.divergenceHint}>
                    Defaults differ across these entities - split by legal type
                    to pre-fill.
                </p>
            ) : null}
        </div>
    );
};
